/**
 * Categories utility - fetches and manages tag categories from API
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Cache for categories
let categoriesCache = null;
let categoriesPromise = null;

/**
 * Fetch categories from API (with caching)
 */
export async function fetchCategories() {
  // Return cached promise if already fetching
  if (categoriesPromise) {
    return categoriesPromise;
  }

  // Return cached data if available
  if (categoriesCache) {
    return categoriesCache;
  }

  // Fetch from API
  categoriesPromise = axios.get(`${API_BASE_URL}/categories`)
    .then(response => {
      if (response.data.success) {
        categoriesCache = response.data.data;
        return categoriesCache;
      }
      throw new Error('Failed to fetch categories');
    })
    .catch(error => {
      categoriesPromise = null;
      console.error('Error fetching categories:', error);
      // Return default categories as fallback
      return getDefaultCategories();
    });

  return categoriesPromise;
}

/**
 * Clear categories cache (call after updating categories)
 */
export function clearCategoriesCache() {
  categoriesCache = null;
  categoriesPromise = null;
}

// Make clearCategoriesCache available globally for Settings page
if (typeof window !== 'undefined') {
  window.clearCategoriesCache = clearCategoriesCache;
}

/**
 * Get default categories (fallback if API fails)
 */
function getDefaultCategories() {
  return {
    categories: [
      { name: 'Animals and Wildlife', color: 'bg-green-100 text-green-800 border-green-300' },
      { name: 'Abstract and Geometric', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      { name: 'Nature and Plants', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
      { name: 'Hearts and Symbols', color: 'bg-pink-100 text-pink-800 border-pink-300' },
      { name: 'Holiday and Seasonal', color: 'bg-red-100 text-red-800 border-red-300' },
      { name: 'Ocean and Marine', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      { name: 'Desert and Southwest', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      { name: 'Digital and Modern', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
      { name: 'Hidden and Optical Illusion', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      { name: 'Cultural and Themed', color: 'bg-amber-100 text-amber-800 border-amber-300' },
      { name: 'Emotions and Concepts', color: 'bg-rose-100 text-rose-800 border-rose-300' },
      { name: 'Art Styles', color: 'bg-gray-100 text-gray-800 border-gray-300' },
      { name: 'Special Occasions', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
      { name: 'Machines and Things', color: 'bg-slate-100 text-slate-800 border-slate-300' }
    ],
    defaultCategory: 'Abstract and Geometric',
    priorityCategories: ['Holiday and Seasonal', 'Hidden and Optical Illusion', 'Ocean and Marine']
  };
}

/**
 * Get category color from fetched categories
 */
export async function getCategoryColor(category) {
  const categories = await fetchCategories();
  const categoryData = categories.categories.find(c => c.name === category);
  return categoryData?.color || 'bg-gray-100 text-gray-800 border-gray-300';
}

/**
 * Get category color synchronously (uses cache if available)
 */
export function getCategoryColorSync(category) {
  if (categoriesCache) {
    const categoryData = categoriesCache.categories.find(c => c.name === category);
    return categoryData?.color || 'bg-gray-100 text-gray-800 border-gray-300';
  }
  // Fallback to default colors if cache not loaded
  const defaultColors = {
    'Animals and Wildlife': 'bg-green-100 text-green-800 border-green-300',
    'Abstract and Geometric': 'bg-purple-100 text-purple-800 border-purple-300',
    'Nature and Plants': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    'Hearts and Symbols': 'bg-pink-100 text-pink-800 border-pink-300',
    'Holiday and Seasonal': 'bg-red-100 text-red-800 border-red-300',
    'Ocean and Marine': 'bg-blue-100 text-blue-800 border-blue-300',
    'Desert and Southwest': 'bg-orange-100 text-orange-800 border-orange-300',
    'Digital and Modern': 'bg-indigo-100 text-indigo-800 border-indigo-300',
    'Hidden and Optical Illusion': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Cultural and Themed': 'bg-amber-100 text-amber-800 border-amber-300',
    'Emotions and Concepts': 'bg-rose-100 text-rose-800 border-rose-300',
    'Art Styles': 'bg-gray-100 text-gray-800 border-gray-300',
    'Special Occasions': 'bg-cyan-100 text-cyan-800 border-cyan-300',
    'Machines and Things': 'bg-slate-100 text-slate-800 border-slate-300'
  };
  return defaultColors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
}

